import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Store, Check, Loader2, ArrowRight } from "lucide-react";

export default function VendorOnboard() {
  const [f, setF] = useState({
    business_name: "", contact_name: "", email: "", password: "", phone: "",
    gstin: "", pan_number: "", bank_account_number: "", ifsc_code: "", plan_key: "",
    country_code: "IN", state: "", district: "", city: "", postal_code: "", service_radius_km: 10,
  });
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [plans, setPlans] = useState([]);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => { api.get("/geo/countries").then(r => setCountries(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/membership/plans/store").then(r => { setPlans(r.data); if (r.data[0]) setF(v => ({ ...v, plan_key: r.data[0].key })); }).catch(() => {}); }, []);
  useEffect(() => { setF(v=>({...v,state:"",district:""})); setStates([]); setDistricts([]); if(f.country_code) api.get("/geo/states",{params:{country_code:f.country_code}}).then(r=>setStates(r.data)); }, [f.country_code]);
  useEffect(() => { setF(v=>({...v,district:""})); setDistricts([]); if(f.country_code&&f.state) api.get("/geo/districts",{params:{country_code:f.country_code,state:f.state}}).then(r=>setDistricts(r.data)); }, [f.state]);

  const submit = async () => {
    if (!f.business_name || !f.contact_name || !f.email || f.password.length < 6 || !f.postal_code) {
      toast.error("Business name, contact, email, 6+ char password and postal code are required"); return;
    }
    setBusy(true);
    try {
      const r = await api.post("/store/vendor/onboard", { ...f, service_radius_km: parseFloat(f.service_radius_km) });
      setDone(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Onboarding failed"); }
    finally { setBusy(false); }
  };

  if (done) return (
    <div className="min-h-screen bg-[#FAF7F0] grid place-items-center p-6">
      <div className="nb-card bg-[#86EFAC] p-8 max-w-lg w-full space-y-4 text-center" data-testid="vendor-onboard-success">
        <div className="w-16 h-16 rounded-full bg-white border-2 border-black grid place-items-center mx-auto"><Check size={32} /></div>
        <h1 className="display text-3xl font-black">Store Registered!</h1>
        <p className="text-sm">Your bookstall workspace is live. KYC is <b>{done.kyc_status}</b> — you can start adding products now.</p>
        <div className="nb-card bg-white p-4 text-left text-sm mono"><div>Login: <b>{done.email}</b></div><div>Vendor ID: <b>{done.vendor_id.slice(0, 8)}</b></div></div>
        <Link to="/login" className="nb-btn w-full justify-center" data-testid="vendor-goto-login">Go to Login <ArrowRight size={16} /></Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAF7F0] p-4 md:p-8">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Store size={28} /><h1 className="display text-3xl font-black">Sell on Schoolz Store</h1></div>
          <Link to="/store" className="text-xs underline" data-testid="vendor-view-store">Browse the store →</Link>
        </div>
        <div className="nb-card bg-white p-6 space-y-3">
          <h2 className="display text-lg font-bold">Business & KYC</h2>
          <input className="nb-input" placeholder="Business Name *" value={f.business_name} onChange={set("business_name")} data-testid="vendor-business-name" />
          <div className="grid md:grid-cols-2 gap-3">
            <input className="nb-input" placeholder="Contact Person *" value={f.contact_name} onChange={set("contact_name")} data-testid="vendor-contact-name" />
            <input className="nb-input mono" placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="vendor-phone" />
            <input className="nb-input" placeholder="Login Email *" value={f.email} onChange={set("email")} data-testid="vendor-email" />
            <input type="password" className="nb-input" placeholder="Password (6+) *" value={f.password} onChange={set("password")} data-testid="vendor-password" />
            <input className="nb-input mono" placeholder="GSTIN" value={f.gstin} onChange={set("gstin")} data-testid="vendor-gstin" />
            <input className="nb-input mono" placeholder="PAN" value={f.pan_number} onChange={set("pan_number")} data-testid="vendor-pan" />
            <input className="nb-input mono" placeholder="Bank Account No." value={f.bank_account_number} onChange={set("bank_account_number")} data-testid="vendor-bank" />
            <input className="nb-input mono" placeholder="IFSC" value={f.ifsc_code} onChange={set("ifsc_code")} data-testid="vendor-ifsc" />
          </div>
          <h2 className="display text-lg font-bold pt-2">Service Area</h2>
          <div className="grid md:grid-cols-2 gap-3">
            <select className="nb-select" value={f.country_code} onChange={set("country_code")} data-testid="vendor-country">
              {countries.map(c => <option key={c.country_code} value={c.country_code}>{c.country_name}</option>)}
            </select>
            <select className="nb-select" value={f.state} onChange={set("state")} disabled={!states.length} data-testid="vendor-state">
              <option value="">Select State *</option>
              {states.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="nb-select" value={f.district} onChange={set("district")} disabled={!districts.length} data-testid="vendor-district">
              <option value="">Select District *</option>
              {districts.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            {f.state && districts.length === 0 &&
              <p className="text-xs text-amber-700 md:col-span-2" data-testid="vendor-nodistricts">No districts added yet — add via Global Admin Territories.</p>}
            <input className="nb-input" placeholder="City" value={f.city} onChange={set("city")} data-testid="vendor-city" />
            <input className="nb-input mono" placeholder="Postal / ZIP *" value={f.postal_code} onChange={set("postal_code")} data-testid="vendor-postal" />
            <input type="number" className="nb-input mono" placeholder="Service radius (km)" value={f.service_radius_km} onChange={set("service_radius_km")} data-testid="vendor-radius" />
          </div>
          <h2 className="display text-lg font-bold pt-2">Merchant Plan</h2>
          <select className="nb-select" value={f.plan_key} onChange={set("plan_key")} data-testid="vendor-plan">
            {plans.map(p => (
              <option key={p.key} value={p.key}>
                {p.name} — {p.currency === "USD" ? "$" : "₹"}{Number(p.effective_price ?? p.base_price).toLocaleString()}/{p.billing_interval === "monthly" ? "mo" : "yr"}{p.commission_pct != null ? ` · ${p.commission_pct}% take-rate` : ""}
              </option>
            ))}
          </select>
          {(() => { const p = plans.find(x => x.key === f.plan_key); return p?.features?.length ? (
            <ul className="text-xs mt-1 space-y-0.5" data-testid="vendor-plan-features">{p.features.map((x, i) => <li key={i}>✓ {x}</li>)}</ul>
          ) : null; })()}
          <button className="nb-btn w-full justify-center" onClick={submit} disabled={busy} data-testid="vendor-submit">
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Register My Store"}
          </button>
        </div>
      </div>
    </div>
  );
}
